// src/pages/hosted/hosted.tsx
import React, { useState } from "react";
import {
  Container,
  Heading,
  Flex,
  Input,
  Select,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  SimpleGrid,
  VStack,
  Text,
  Box,
  Image,
  Badge,
  Stack,
  Divider,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import {
  InfoIcon,
  LinkIcon,
  ViewIcon,
  CalendarIcon,
  TimeIcon,
} from "@chakra-ui/icons";
import { mutate } from "swr";
import { Link } from "react-router-dom";
import { useHosted, HostedEvent } from "../../hooks/useHosted.ts";
import { BaseUrl } from "../../const.ts";
import { OnlineIcon } from "../../icons/OnlineIcon.tsx";
import { InpersonIcon } from "../../icons/InpersonIcon.tsx";
import { LocationIcon } from "../../icons/LocationIcon.tsx";
import { useTranslation } from "react-i18next";

const Hosted: React.FC = () => {
  const { t } = useTranslation();
  const { active, past, isLoading, error } = useHosted();
  const activeEvents: HostedEvent[] = active?.body ?? [];
  const pastEvents:   HostedEvent[] = past?.body   ?? [];

  const [search, setSearch] = useState("");
  const [ticketFilter, setTicketFilter] = useState<"ALL"|"FREE"|"PAID"|"OTHER">("ALL");

  const filterEvents = (events: HostedEvent[]) =>
      events.filter(e => {
        const matchesSearch = e.eventName.toLowerCase().includes(search.toLowerCase());
        const matchesTicket =
            ticketFilter === "ALL" ||
            (ticketFilter === "FREE"  && e.ticketType === "FREE")  ||
            (ticketFilter === "PAID"  && e.ticketType === "PAID")  ||
            (ticketFilter === "OTHER" && e.ticketType === "OTHER");
        return matchesSearch && matchesTicket;
      });

  // sort comparator: newest startDate+startTime first
  const sortByStart = (a: HostedEvent, b: HostedEvent) =>
      new Date(`${b.startDate}T${b.startTime}`).getTime()
      - new Date(`${a.startDate}T${a.startTime}`).getTime();

  const sortedActive = filterEvents(activeEvents).sort(sortByStart);
  const sortedPast   = filterEvents(pastEvents).sort(sortByStart);

  const renderList = (events: HostedEvent[], emptyMsg: string) =>
      events.length ? (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {events.map(ev => <CardItem key={ev.idEvent} {...ev} />)}
          </SimpleGrid>
      ) : (
          <VStack h="200px" justify="center">
            <InfoIcon boxSize={12} color="gray.400" />
            <Text fontSize="lg">{emptyMsg}</Text>
          </VStack>
      );

  if (isLoading) {
    return (
        <Container maxW="container.xl" py={10} pt="10%" textAlign="center">
          <Spinner size="xl" />
        </Container>
    );
  }

  if (error) {
    return (
        <Container maxW="container.xl" py={10} pt="10%">
          <Alert status="error" flexDirection="column" textAlign="center" alignItems="center">
            <AlertIcon boxSize="40px" />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              {t("Error loading events")}
            </AlertTitle>
            <AlertDescription mb={4}>
              {t("Please try again")}
            </AlertDescription>
            <Button
                onClick={() => {
                  mutate("recommended-ms/host-active");
                  mutate("recommended-ms/host-past");
                }}
                colorScheme="teal"
            >
              {t("Retry")}
            </Button>
          </Alert>
        </Container>
    );
  }

  return (
      <Container maxW="container.xl" py={10} pt="10%">
        <Heading mb={6}>{t("hosted_events")}</Heading>

        <Flex mb={4} gap={4} wrap="wrap">
          <Input
              placeholder={t("SearchEvent") || "Search…"}
              value={search}
              onChange={e => setSearch(e.target.value)}
              maxW="300px"
          />
          <Select
              maxW="200px"
              value={ticketFilter}
              onChange={e => setTicketFilter(e.target.value as any)}
          >
            <option value="ALL">{t("Clear Filter") || "All"}</option>
            <option value="FREE">{t("Free")}</option>
            <option value="PAID">{t("Paid")}</option>
            <option value="OTHER">{t("Other")}</option>
          </Select>
        </Flex>

        <Tabs isFitted variant="enclosed" colorScheme="teal">
          <TabList mb={4}>
            <Tab>{t("upcoming")}</Tab>
            <Tab>{t("past")}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              {renderList(sortedActive, t("no_active_hosted"))}
            </TabPanel>
            <TabPanel>
              {renderList(sortedPast,   t("no_past_hosted"))}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>
  );
};

const formatDate = (d: string) =>
    new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
const formatTime = (t: string) =>
    new Date(`1970-01-01T${t}`).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

const CardItem: React.FC<HostedEvent> = ({
                                           idEvent,
                                           coverPhoto,
                                           eventName,
                                           ticketType,
                                           ticketCost,
                                           attendanceTypeEnum,
                                           startDate,
                                           endDate = null,
                                           startTime,
                                           endTime = null,
                                           duration = null,
                                           address = null,
                                           viewCount,
                                           usersList,
                                         }) => {
  const { t } = useTranslation();

  return (
      <Box
          borderWidth="1px"
          borderRadius="lg"
          overflow="hidden"
          boxShadow="0px 10px 25px rgba(0, 0, 0, 0.04)"
          _hover={{ boxShadow: "xl", transform: "scale(1.03)" }}
          transition="all 0.3s ease-in-out"
          maxW="sm"
          bg="white"
          position="relative"
          pt="10%"
      >
        <Link to={`/details-hosted/${idEvent}`}>
          <Image
              src={`${BaseUrl}/images/${coverPhoto}`}
              alt={eventName}
              w="100%"
              h="300px"
              objectFit="cover"
          />
          <Box position="absolute" top="300px" left="3" display="flex" gap="6px">
            <Badge
                bg={ticketType === "FREE" ? "green.500" : ticketType === "OTHER" ? "white" : "yellow.600"}
                color={ticketType === "OTHER" ? "blue.600" : "white"}
                px="8px"
                py="4px"
                borderRadius="md"
            >
              {ticketType === "PAID"
                  ? `${ticketCost?.toFixed(2)} ₼`
                  : ticketType === "OTHER"
                      ? <Flex align="center"><LinkIcon mr="2"/>{t("OTHER")}</Flex>
                      : t("FREE")}
            </Badge>
            <Badge bg="gray.800" color="white" px="8px" py="4px" borderRadius="md">
              <Flex align="center">
                {attendanceTypeEnum === "ONLINE" ? <OnlineIcon /> : <InpersonIcon />}
                <Text ml="2" fontSize="sm">
                  {attendanceTypeEnum === "ONLINE" ? t("Online") : t("Offline")}
                </Text>
              </Flex>
            </Badge>
            <Badge bg="gray.700" color="white" px="8px" py="4px" borderRadius="md">
              <Flex align="center">
                <ViewIcon mr="2" />
                <Text fontSize="sm">{viewCount}</Text>
              </Flex>
            </Badge>
          </Box>
        </Link>

        <Box p="4">
          <Stack spacing="3">
            <Link to={`/details-hosted/${idEvent}`}>
              <Heading
                  size="md"
                  fontWeight="700"
                  _hover={{ textDecoration: "underline", color: "blue.600" }}
                  transition="color 0.2s"
              >
                {eventName}
              </Heading>
            </Link>

            <SimpleGrid columns={2} spacing={2}>
              <Flex align="center">
                <CalendarIcon mr="2" />
                <Text fontSize="sm">
                  {formatDate(startDate)}{endDate ? ` – ${formatDate(endDate)}` : ""}
                </Text>
              </Flex>
              <Flex align="center">
                <TimeIcon mr="2" />
                <Text fontSize="sm">
                  {formatTime(startTime)}{endTime ? ` – ${formatTime(endTime)}` : ""}
                </Text>
              </Flex>
              {duration != null && (
                  <Flex align="center">
                    <Text fontSize="sm">
                      {duration} {t("Minutes")}
                    </Text>
                  </Flex>
              )}
              <Flex align="center">
                <LocationIcon mr="2" />
                <Text fontSize="sm">{address ?? t("Location")}</Text>
              </Flex>
            </SimpleGrid>

            <Divider />

            <SimpleGrid columns={3} spacing={2} textAlign="center">
              <VStack>
                <Text fontSize="xs" color="gray.500">{t("Going")}</Text>
                <Text fontWeight="bold">{usersList.goingCount}</Text>
              </VStack>
              <VStack>
                <Text fontSize="xs" color="gray.500">{t("Interested")}</Text>
                <Text fontWeight="bold">{usersList.interestedCount}</Text>
              </VStack>
              <VStack>
                <Text fontSize="xs" color="gray.500">{t("Invited")}</Text>
                <Text fontWeight="bold">{usersList.expectedCount}</Text>
              </VStack>
            </SimpleGrid>

            <Divider />

            <Stack direction="row" justify="center" w="100%">
              <Button
                  as={Link}
                  to={`/details-hosted/${idEvent}`}
                  bg="#111833"
                  color="white"
                  size="md"
                  w="100%"
                  _hover={{ bg: "#0e102a" }}
              >
                {t("view_statistics_management")}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
  );
};

export default Hosted;
